import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { SetBackgroundTool } from 'js/src/agent/tools/SetBackgroundTool';
import { SetGlobalColorGradingTool } from 'js/src/agent/tools/SetGlobalColorGradingTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';
import { ApplyBrandKitTool } from 'js/src/agent/tools/brand-kit/ApplyBrandKitTool';
import { ExtractBrandTool } from 'js/src/agent/tools/brand-kit/ExtractBrandTool';
import { ListBrandKitsTool } from 'js/src/agent/tools/brand-kit/ListBrandKitsTool';
import { SaveBrandKitTool } from 'js/src/agent/tools/brand-kit/SaveBrandKitTool';
import { SketchResearchTool } from 'js/src/agent/tools/sketch/SketchResearchTool';

export const createBrandKitSkill: Skill = {
    name: SKILL_NAMES.createBrandKit,
    description:
        'Load BEFORE acting on any branding request — applying a kit, using their brand colors, or building one from their website (load it before asking for the URL).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Applying and Building Brand Kits

**Colors come from a brand kit or the template — never hand-picked.** With no kit,
the template's built-in colors and styling stand as-is: no \`${SetBackgroundTool.toolName}\`/
\`${SetTextTool.toolName}\` color overrides, no \`${SetGlobalColorGradingTool.toolName}\` to "fix" contrast, unless
the user specifically asks for a change.

## Applying an existing kit
Check \`${ListBrandKitsTool.toolName}\`. If a kit exists — or the user references their brand —
apply it with \`${ApplyBrandKitTool.toolName}\` (early, so everything built afterwards inherits it).

## Building a kit from a website
When the user wants a kit and none exists:
- With a URL, call \`${ExtractBrandTool.toolName}\` on it. With only a brand/company name,
  first find their site with \`${SketchResearchTool.toolName}\` — 2-3 URLs across brand surfaces
  (homepage, blog, social profile) reveal different aspects, and extract them in
  parallel.
- Curate the results (below), then \`${SaveBrandKitTool.toolName}\` and **confirm the result with
  the user** before treating it as their brand. \`${ApplyBrandKitTool.toolName}\` once confirmed.

### Validate the extraction first
Check the screenshot and data for a failed extraction: a Cloudflare/bot challenge,
an error or parking page, or a mostly blank/unstyled page. If it looks like
garbage, try alternative URLs (other subdomains, their social pages) — and if
nothing usable comes back, tell the user the site blocked extraction instead of
saving low-confidence data. **No kit beats a wrong one.**

### Logo
Pick the candidate that visually looks like the brand's logo. Reject og-image
social previews, and reject the host platform's own icons when extracting from a
social profile (unless the platform IS the brand). Don't reject a logo for being
small; favicons and apple-touch-icons are a legitimate fallback. Only report "no
logo found" when zero candidates belong to the brand.

### Colors
**The screenshot is ground truth**: pick the 3-5 most prominent, saturated colors
visible in the page design itself (not in photos or ads), then corroborate exact
hex values from the extraction's UI sources and CSS variables. Eliminate CSS
framework defaults (Bootstrap-style red/yellow/green/blue), variables that match
nothing visible, near-duplicate shades, and host-platform colors. Anchors: the
near-black used for text (dark) and the near-white background (light) — brand
colors must be clearly chromatic and distinct from both.

### Fonts
Use the extracted heading and body fonts; ignore generic families (serif,
sans-serif, monospace, system-ui, etc.). Null is fine when nothing real was found.

## Template pairing
A brand kit is a hard requirement to check during template choice: verify the
template works with the kit before committing to it, not after the video is built.`;
    },
};
